import { ethers } from 'ethers'
import {
  AERO_POOL_FACTORY,
  AERO_ROUTER,
  CL3_FACTORY,
  CL3_QUOTER,
  CL_FACTORY,
  CL_QUOTER,
  CL_TICK_SPACINGS_F1,
  CL_TICK_SPACINGS_F3,
  MULTICALL3,
  UNI_V3_FACTORY,
  UNI_V3_FEES,
  UNI_V3_QUOTER,
  USDC
} from 'utils/swap/addresses'
import {
  AERO_POOL_FACTORY_ABI,
  AERO_ROUTER_ABI,
  CL_FACTORY_ABI,
  CL_QUOTER_ABI,
  ERC20_ABI,
  MULTICALL3_ABI,
  UNI_V3_FACTORY_ABI,
  UNI_V3_QUOTER_ABI
} from 'utils/swap/abi'
import type { OnChainVenue } from 'utils/swap/types'
import { STOCKS, type Stock } from './catalogue'
import {
  PROBE_USDC,
  pickQuotingPool,
  poolLiquidityUsd,
  premiumBps,
  priceFromFeed,
  priceFromQuote,
  type StockPool
} from './pricing'

/**
 * The stock board's data, read from Base in two round trips and nothing else.
 *
 * Two, not one, because the second question depends on the first answer: which
 * pools exist, then what those pools hold and what they would pay. Both are
 * `aggregate3`, so thirteen companies across four venues cost a handful of RPC
 * calls rather than three hundred — which is the difference between a board
 * that loads and a board that exhausts the gateway's budget every twenty
 * seconds.
 *
 * No backend is in this path, deliberately and for the same reason the swap has
 * none: a page that cannot show a price until a service of ours answers is a
 * page that goes dark when that service does.
 */

const multicall = new ethers.utils.Interface(MULTICALL3_ABI as unknown as string[])
const clFactory = new ethers.utils.Interface(CL_FACTORY_ABI as unknown as string[])
const uniFactory = new ethers.utils.Interface(UNI_V3_FACTORY_ABI as unknown as string[])
const aeroFactory = new ethers.utils.Interface(AERO_POOL_FACTORY_ABI as unknown as string[])
const clQuoter = new ethers.utils.Interface(CL_QUOTER_ABI as unknown as string[])
const uniQuoter = new ethers.utils.Interface(UNI_V3_QUOTER_ABI as unknown as string[])
const aeroRouter = new ethers.utils.Interface(AERO_ROUTER_ABI as unknown as string[])
const erc20 = new ethers.utils.Interface(ERC20_ABI as unknown as string[])

const FEED_ABI = [
  'function latestRoundData() view returns (uint80 roundId, int256 answer, uint256 startedAt, uint256 updatedAt, uint80 answeredInRound)'
] as const
const feed = new ethers.utils.Interface(FEED_ABI as unknown as string[])

const B20_ASSET_ABI = ['function multiplier() view returns (uint256)'] as const
const b20 = new ethers.utils.Interface(B20_ASSET_ABI as unknown as string[])

export interface StockMarket {
  stock: Stock
  /** Dollars per share as the deepest answering pool would sell it. */
  poolPrice: number | null
  /** Dollars per share as Chainlink last published it. */
  oraclePrice: number | null
  /** Unix seconds of that publication. */
  oracleUpdatedAt: number | null
  /** Positive when the pool is dearer than the oracle. */
  premiumBps: number | null
  /** `multiplier()` in WAD; 1e18 means one token is one share. */
  multiplier: bigint | null
  /** Every pool found, deepest first. */
  pools: StockPool[]
  /** The pool the headline price came from. */
  quotingPool: StockPool | null
  /** USDC plus the oracle value of the stock side, across every pool. */
  liquidityUsd: number
}

type Row = { success: boolean; returnData: string }

/**
 * How many calls ride in one `aggregate3`, by how expensive they are.
 *
 * A `getPool` is a storage read; a quoter call executes the swap and reverts to
 * return the number, and a hundred of those in one `eth_call` exceeds the gas
 * cap of a node that is not the one you tested on. Measured 2026-09-11: a batch
 * of 160 quoter calls answers on `mainnet.base.org` and **reverts with no data**
 * on `base-rpc.publicnode.com` — and the gateway rotates between providers, so
 * a size that works on one of them is not a size that works.
 */
const CHUNK_CHEAP = 200
const CHUNK_QUOTING = 40

async function aggregate(
  provider: ethers.providers.Provider,
  calls: Array<{ target: string; allowFailure: true; callData: string }>,
  chunkSize: number
): Promise<Row[]> {
  if (calls.length === 0) return []
  const contract = new ethers.Contract(MULTICALL3, multicall, provider)
  const chunks: Array<typeof calls> = []
  for (let i = 0; i < calls.length; i += chunkSize) chunks.push(calls.slice(i, i + chunkSize))
  const results = await Promise.all(chunks.map((chunk) => callWithOneRetry(contract, chunk)))
  return results.flat()
}

/**
 * One retry, after a pause.
 *
 * Several fat multicalls leaving at once is exactly the shape a rate limiter
 * answers with 429, and one refused chunk would otherwise cost the whole board
 * its numbers. Once only: a page that keeps retrying is the reason the limit
 * exists.
 */
async function callWithOneRetry(
  contract: ethers.Contract,
  chunk: Array<{ target: string; allowFailure: true; callData: string }>
): Promise<Row[]> {
  try {
    return (await contract.callStatic.aggregate3(chunk)) as Row[]
  } catch {
    await new Promise((resolve) => setTimeout(resolve, 1200))
    return (await contract.callStatic.aggregate3(chunk)) as Row[]
  }
}

function decodeAddress(row: Row | undefined): string | null {
  if (!row || !row.success) return null
  try {
    const value = String(ethers.utils.defaultAbiCoder.decode(['address'], row.returnData)[0]).toLowerCase()
    return value === ethers.constants.AddressZero ? null : value
  } catch {
    return null
  }
}

function decodeUint(row: Row | undefined): bigint | null {
  if (!row || !row.success) return null
  try {
    return ethers.utils.defaultAbiCoder.decode(['uint256'], row.returnData)[0].toBigInt() as bigint
  } catch {
    return null
  }
}

interface PoolDescriptor {
  slug: string
  venue: OnChainVenue
  stable?: boolean
  fee?: number
  tickSpacing?: number
}

/** Which (stock, USDC) pools might exist. One row each; absence is the zero address. */
function discoveryCalls(stocks: Stock[]): {
  calls: Array<{ target: string; allowFailure: true; callData: string }>
  descriptors: PoolDescriptor[]
} {
  const calls: Array<{ target: string; allowFailure: true; callData: string }> = []
  const descriptors: PoolDescriptor[] = []
  for (const stock of stocks) {
    const token = stock.address
    for (const tickSpacing of CL_TICK_SPACINGS_F3) {
      calls.push({
        target: CL3_FACTORY,
        allowFailure: true,
        callData: clFactory.encodeFunctionData('getPool', [token, USDC, tickSpacing])
      })
      descriptors.push({ slug: stock.slug, venue: 'aero-cl3', tickSpacing })
    }
    for (const tickSpacing of CL_TICK_SPACINGS_F1) {
      calls.push({
        target: CL_FACTORY,
        allowFailure: true,
        callData: clFactory.encodeFunctionData('getPool', [token, USDC, tickSpacing])
      })
      descriptors.push({ slug: stock.slug, venue: 'aero-cl', tickSpacing })
    }
    for (const fee of UNI_V3_FEES) {
      calls.push({
        target: UNI_V3_FACTORY,
        allowFailure: true,
        callData: uniFactory.encodeFunctionData('getPool', [token, USDC, fee])
      })
      descriptors.push({ slug: stock.slug, venue: 'uni-v3', fee })
    }
    for (const stable of [false, true]) {
      calls.push({
        target: AERO_POOL_FACTORY,
        allowFailure: true,
        callData: aeroFactory.encodeFunctionData('getPool', [token, USDC, stable])
      })
      descriptors.push({ slug: stock.slug, venue: 'aero-v2', stable })
    }
  }
  return { calls, descriptors }
}

/** The quote call that prices one pool: 100 USDC in, however much comes out. */
function quoteCallFor(
  stock: Stock,
  pool: PoolDescriptor
): { target: string; allowFailure: true; callData: string } {
  switch (pool.venue) {
    case 'aero-cl':
    case 'aero-cl3':
      return {
        target: pool.venue === 'aero-cl3' ? CL3_QUOTER : CL_QUOTER,
        allowFailure: true,
        callData: clQuoter.encodeFunctionData('quoteExactInputSingle', [
          {
            tokenIn: USDC,
            tokenOut: stock.address,
            amountIn: PROBE_USDC,
            tickSpacing: pool.tickSpacing,
            sqrtPriceLimitX96: 0
          }
        ])
      }
    case 'uni-v3':
      return {
        target: UNI_V3_QUOTER,
        allowFailure: true,
        callData: uniQuoter.encodeFunctionData('quoteExactInputSingle', [
          {
            tokenIn: USDC,
            tokenOut: stock.address,
            amountIn: PROBE_USDC,
            fee: pool.fee,
            sqrtPriceLimitX96: 0
          }
        ])
      }
    default:
      return {
        target: AERO_ROUTER,
        allowFailure: true,
        callData: aeroRouter.encodeFunctionData('getAmountsOut', [
          PROBE_USDC,
          [{ from: USDC, to: stock.address, stable: Boolean(pool.stable), factory: AERO_POOL_FACTORY }]
        ])
      }
  }
}

function decodeQuote(venue: OnChainVenue, row: Row | undefined): bigint | null {
  if (!row || !row.success) return null
  try {
    if (venue === 'aero-v2') {
      const [amounts] = aeroRouter.decodeFunctionResult('getAmountsOut', row.returnData)
      const last = amounts[amounts.length - 1]
      return last ? (last.toBigInt() as bigint) : null
    }
    const iface = venue === 'uni-v3' ? uniQuoter : clQuoter
    const [amountOut] = iface.decodeFunctionResult('quoteExactInputSingle', row.returnData)
    return amountOut.toBigInt() as bigint
  } catch {
    return null
  }
}

/**
 * The whole board, or one company when `only` names it.
 *
 * Every failure is local: a feed that will not answer costs that stock its
 * oracle column, a pool that reverts costs that row its price, and neither
 * takes the page down. That is why every call is `allowFailure`.
 */
export async function readStockMarkets(
  provider: ethers.providers.Provider,
  only?: string[]
): Promise<StockMarket[]> {
  const stocks = only ? STOCKS.filter((s) => only.includes(s.slug)) : STOCKS
  if (stocks.length === 0) return []

  const { calls, descriptors } = discoveryCalls(stocks)
  const discovered = await aggregate(provider, calls, CHUNK_CHEAP)

  const found: Array<{ descriptor: PoolDescriptor; address: string; stock: Stock }> = []
  const byStock = new Map(stocks.map((s) => [s.slug, s]))
  descriptors.forEach((descriptor, index) => {
    const address = decodeAddress(discovered[index])
    if (!address) return
    const stock = byStock.get(descriptor.slug)
    if (stock) found.push({ descriptor, address, stock })
  })

  /*
   * Second batch: what each pool holds, what it would pay, and — once per
   * company — the oracle and the multiplier. They are asked together because
   * none of them depends on another's answer.
   */
  const second: Array<{ target: string; allowFailure: true; callData: string }> = []
  const poolRowIndex: number[] = []
  for (const entry of found) {
    poolRowIndex.push(second.length)
    second.push({
      target: USDC,
      allowFailure: true,
      callData: erc20.encodeFunctionData('balanceOf', [entry.address])
    })
    second.push({
      target: entry.stock.address,
      allowFailure: true,
      callData: erc20.encodeFunctionData('balanceOf', [entry.address])
    })
    second.push(quoteCallFor(entry.stock, entry.descriptor))
  }
  const stockRowIndex = new Map<string, number>()
  for (const stock of stocks) {
    stockRowIndex.set(stock.slug, second.length)
    second.push({
      target: stock.feed,
      allowFailure: true,
      callData: feed.encodeFunctionData('latestRoundData', [])
    })
    second.push({
      target: stock.address,
      allowFailure: true,
      callData: b20.encodeFunctionData('multiplier', [])
    })
  }
  const detail = await aggregate(provider, second, CHUNK_QUOTING)

  const poolsBySlug = new Map<string, StockPool[]>()
  found.forEach((entry, i) => {
    const base = poolRowIndex[i]
    const pool: StockPool = {
      venue: entry.descriptor.venue,
      address: entry.address,
      ...(entry.descriptor.stable === undefined ? {} : { stable: entry.descriptor.stable }),
      ...(entry.descriptor.fee === undefined ? {} : { fee: entry.descriptor.fee }),
      ...(entry.descriptor.tickSpacing === undefined ? {} : { tickSpacing: entry.descriptor.tickSpacing }),
      reserveQuote: decodeUint(detail[base]) ?? 0n,
      reserveBase: decodeUint(detail[base + 1]) ?? 0n,
      quotedOut: decodeQuote(entry.descriptor.venue, detail[base + 2])
    }
    const list = poolsBySlug.get(entry.stock.slug)
    if (list) list.push(pool)
    else poolsBySlug.set(entry.stock.slug, [pool])
  })

  return stocks.map((stock) => {
    const row = stockRowIndex.get(stock.slug) ?? 0
    const feedRow = detail[row]
    let answer: bigint | null = null
    let updatedAt: number | null = null
    if (feedRow?.success) {
      try {
        const decoded = feed.decodeFunctionResult('latestRoundData', feedRow.returnData)
        answer = decoded.answer.toBigInt() as bigint
        updatedAt = Number(decoded.updatedAt.toString())
      } catch {
        answer = null
      }
    }
    const oraclePrice = priceFromFeed(answer)
    const pools = (poolsBySlug.get(stock.slug) ?? []).sort(
      (a, b) =>
        poolLiquidityUsd(b, oraclePrice, stock.decimals) - poolLiquidityUsd(a, oraclePrice, stock.decimals)
    )
    const quotingPool = pickQuotingPool(pools, oraclePrice, stock.decimals)
    const poolPrice = quotingPool
      ? priceFromQuote(PROBE_USDC, quotingPool.quotedOut, stock.decimals)
      : null
    return {
      stock,
      poolPrice,
      oraclePrice,
      oracleUpdatedAt: updatedAt,
      premiumBps: premiumBps(poolPrice, oraclePrice),
      multiplier: decodeUint(detail[row + 1]),
      pools,
      quotingPool,
      liquidityUsd: pools.reduce((sum, p) => sum + poolLiquidityUsd(p, oraclePrice, stock.decimals), 0)
    }
  })
}
